import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useRecipe } from '../context/RecipeContext';
import landingBackground from '../images/landingbackground3.jpg';

const TermsAndConditions: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  const handleGetStarted = () => {
    setMode('generate');
    navigate('/composer');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
      {/* Hero Section */}
      <Box
        sx={{
          height: { xs: '40vh', md: '50vh' },
          backgroundImage: `url(${landingBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(1px)',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 2,
            }}
          >
            Terms & Conditions
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 1,
            }}
          >
            Rules for Using Our Service
          </Typography>
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            textAlign: 'center',
            mb: 4,
            fontWeight: 'bold',
            color: 'primary.main'
          }}
        >
          Terms of Service
        </Typography>

        <Typography
          variant="body1"
          sx={{
            mb: 4,
            textAlign: 'center',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6,
            fontSize: { xs: '1rem', md: '1.1rem' }
          }}
        >
          These Terms and Conditions govern your use of Create A Dish and outline the rules and regulations
          for the use of our website and services.
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Acceptance of Terms</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                By accessing and using Create A Dish, you accept and agree to be bound by the terms and provision
                of this agreement. If you do not agree to abide by the above, please do not use this service.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Use License</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Permission is granted to temporarily use Create A Dish for personal, non-commercial transitory viewing only.
                This is the grant of a license, not a transfer of title, and under this license you may not:
              </Typography>
              <Typography component="div" variant="body2" sx={{ pl: 2 }}>
                • Modify or copy the materials<br />
                • Use the materials for any commercial purpose or for any public display<br />
                • Attempt to decompile or reverse engineer any software contained on our website<br />
                • Remove any copyright or other proprietary notations from the materials
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">User Responsibilities</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ mb: 2 }}>
                As a user of our service, you agree to:
              </Typography>
              <Typography component="div" variant="body2" sx={{ pl: 2 }}>
                • Provide accurate and complete information<br />
                • Use the service only for lawful purposes<br />
                • Not violate any applicable laws or regulations<br />
                • Respect the rights of other users<br />
                • Not attempt to gain unauthorized access to our systems
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Content and Intellectual Property</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                The service and its original content, features, and functionality are and will remain the exclusive property
                of Create A Dish and its licensors. The service is protected by copyright, trademark, and other laws.
                Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Disclaimer</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                The information on this website is provided on an 'as is' basis. To the fullest extent permitted by law,
                Create A Dish excludes all representations, warranties, conditions and terms whether express or implied,
                statutory or otherwise, including without limitation warranties of merchantability, fitness for a particular
                purpose, and non-infringement.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Limitation of Liability</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                In no event shall Create A Dish, nor its directors, employees, partners, agents, suppliers, or affiliates,
                be liable for any indirect, incidental, special, consequential, or punitive damages, including without
                limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the service.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Contact Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                If you have any questions about these Terms and Conditions, please contact us at{' '}
                <Typography component="span" sx={{ fontWeight: 'bold' }}>info@aisuretech.com</Typography>.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h5"
            component="h3"
            sx={{
              mb: 3,
              fontWeight: 'bold',
              color: 'primary.main'
            }}
          >
            Ready to Create Your Perfect Dish?
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<StarIcon />}
            onClick={handleGetStarted}
            sx={{
              minWidth: 200,
              fontSize: '1.1rem',
              py: 1.5,
              px: 4,
            }}
          >
            Get Started
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            © 2025 Create A Dish. Made with ❤️ for food lovers everywhere.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TermsAndConditions;